import { FC, memo, useCallback, useMemo } from 'react';

import { clsx } from 'clsx';
import Link from 'next/link';

import { useCart } from '@screens/Cart';
import { ICON_SECONDARY } from '@shared/constants/colors';
import { useAppSelector } from '@shared/hooks/redux';
import { ProductCardType } from '@shared/types/product';
import { Checkbox } from '@shared/ui/Checkbox';
import { CountInput, CountInputProps } from '@shared/ui/CountInput/CountInput';
import { ProductRemainder } from '@shared/ui/ProductRemainder/ProductRemainder';
import { getViewPort } from '@shared/ui/ScreenSizeTracking';
import { HStack, VStack } from '@shared/ui/Stack';
import { formatPrice } from '@shared/utils/formatPrice/formatPrice';
import { getSizeNameProduct, getSizeProduct } from '@shared/utils/getSizeProduct/getSizeProduct';
import { FavoriteButton } from '@widgets/ProductCard/ui/FavoriteButton/FavoriteButton';
import { getProductUrl } from '@widgets/ProductCard/utils/getProductUrl';

import { DeleteButton } from '../DeleteButton/DeleteButton';
import { ProductImages } from '../ProductImages/ProductImages';
import cls from './ProductCartItem.module.scss';

type ProductCartItemProps = {
  data: ProductCardType;
};

type FavoritesWrapperProps = {
  productId: string;
  showLabels?: boolean;
  showDeleteButton?: boolean;
  onClickDeleteButton: () => void;
};

const FavoritesWrapper: FC<FavoritesWrapperProps> = ({ productId, showLabels = true, onClickDeleteButton }) => (
  <HStack className={cls.favoritesWrapper}>
    <FavoriteButton
      className={cls.favoriteButton}
      productId={productId}
      iconColor={ICON_SECONDARY}
      iconSize={24}
      label={showLabels ? { add: 'В избранное', remove: 'Удалить из избранного' } : undefined}
    />

    <DeleteButton
      showLabel={showLabels}
      onClick={onClickDeleteButton}
    />
  </HStack>
);

export const ProductCartItem = memo<ProductCartItemProps>(({ data }) => {
  const {
    id,
    name,
    seoName,
    quantity = 0,
    article,
    width,
    depth,
    height,
    images,
    url,
    price,
    remainder,
    selected
  } = data;
  const viewPort = useAppSelector(getViewPort);

  const { removeProducts, selectProducts, changeQuantity } = useCart();

  const isDesktop = viewPort === 'desktop';
  const isMobile = viewPort === 'mobile';
  const inputSize = isDesktop ? 'm' : 's';
  const inStock = remainder !== undefined && +remainder !== 0;

  const correctName = seoName || name;

  const slicedImages = useMemo(() => images?.slice(0, 4), [images]);

  const handleClickCheckbox = useCallback(() => {
    selectProducts(id);
  }, [id, selectProducts]);

  const handleDeleteButton = useCallback(() => {
    removeProducts([id]);
  }, [id, removeProducts]);

  const handleChangeCountInput = useCallback<CountInputProps['onChange']>(
    (quantity) => {
      changeQuantity(id, quantity);
    },
    [changeQuantity, id]
  );

  return (
    <div className={cls.container}>
      <Checkbox
        disabled={!inStock}
        value={selected || false}
        onChange={handleClickCheckbox}
      />
      <HStack
        className={clsx(cls.wrapper, { [cls.disabled]: !inStock })}
        align={'start'}
      >
        <HStack
          align={'start'}
          justify={'between'}
        >
          <HStack align={'start'}>
            <ProductImages
              images={slicedImages}
              url={id && url ? getProductUrl(id, url) : ''}
              productName={name}
              advantages={data.advantages}
            />
          </HStack>
          {isMobile && (
            <FavoritesWrapper
              productId={data.id}
              showLabels={false}
              onClickDeleteButton={handleDeleteButton}
            />
          )}
        </HStack>
        <HStack
          className={cls.rightContent}
          align={'start'}
        >
          <div className={cls.mainContent}>
            <VStack className={cls.aboutProduct}>
              {correctName && (
                <h3 className={cls.aboutProductTitle}>
                  <Link href={id && url ? getProductUrl(id, url) : ''}>{correctName}</Link>
                </h3>
              )}
              {isMobile && <div className={cls.amount}>{formatPrice(quantity * price)}</div>}
              <HStack className={cls.aboutProductInfo}>
                {article && (
                  <div>
                    <span className={cls.aboutProductSecondaryText}>Артикул: </span>
                    <span className={cls.aboutProductText}>{article}</span>
                  </div>
                )}
                <div>
                  <span className={cls.aboutProductSecondaryText}>
                    {getSizeNameProduct({
                      width: width?.value,
                      depth: depth?.value,
                      height: height?.value
                    })}
                  </span>
                  <span className={cls.aboutProductText}>
                    {getSizeProduct({
                      width: width?.value,
                      depth: depth?.value,
                      height: height?.value
                    })}
                  </span>
                </div>
              </HStack>
              {!isMobile && (
                <FavoritesWrapper
                  productId={data.id}
                  onClickDeleteButton={handleDeleteButton}
                />
              )}
              <HStack className={cls.bottomWrapper}>
                {inStock && (
                  <CountInput
                    size={inputSize}
                    min={1}
                    value={quantity}
                    onChange={handleChangeCountInput}
                  />
                )}
                <ProductRemainder
                  className={cls.remainder}
                  remainder={remainder}
                />
              </HStack>
            </VStack>

            {!isMobile && <div className={cls.amount}>{formatPrice(quantity * price)}</div>}
          </div>
        </HStack>
      </HStack>
    </div>
  );
});
